import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { Clock, Minus, Plus } from "lucide-react";
import { fetchExtract, extractShareUrl, extractGenreLabel } from "../lib/api";
import { ExtractContent, extractToPlainText } from "../components/extract/ExtractContent";
import { ListenControls } from "../components/extract/ListenControls";
import { ExtractCTA } from "../components/extract/ExtractCTA";
import { SocialShareButtons } from "../components/SocialShareButtons";
import { useLaunchOffer } from "../components/extract/useLaunchOffer";
import { ReadingSurvey } from "../components/extract/ReadingSurvey";
import { NewsletterSignup } from "../components/extract/NewsletterSignup";
import { useReadingTracker } from "../components/extract/useReadingTracker";

const SIZES = [17, 19, 22];

function useReadingState(ctaRef, ready) {
  const [progress, setProgress] = useState(0);
  const [ctaVisible, setCtaVisible] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const max = document.documentElement.scrollHeight - window.innerHeight;
      setProgress(max > 0 ? Math.min(1, window.scrollY / max) : 0);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => {
    if (!ctaRef.current) return;
    const obs = new IntersectionObserver(([e]) => setCtaVisible(e.isIntersecting));
    obs.observe(ctaRef.current);
    return () => obs.disconnect();
  }, [ctaRef, ready]);
  return { progress, ctaVisible };
}

export default function ExtractRead() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const lieu = params.get("lieu");
  const [extract, setExtract] = useState(null);
  const [error, setError] = useState(false);
  const [size, setSize] = useState(1);
  const ctaRef = useRef(null);
  const { progress, ctaVisible } = useReadingState(ctaRef, !!extract);
  const tracker = useReadingTracker(slug, lieu, !!extract);
  const { markScroll, markCompleted } = tracker;
  const offer = useLaunchOffer(slug, tracker.sessionId, ctaVisible);

  useEffect(() => {
    fetchExtract(slug, false).then((d) => { setExtract(d); document.title = `${d.title} — Extrait offert | CLED`; }).catch(() => setError(true));
  }, [slug]);
  useEffect(() => { markScroll(progress); }, [progress, markScroll]);
  useEffect(() => { if (ctaVisible) markCompleted(); }, [ctaVisible, markCompleted, tracker.sessionId]);

  if (error) return <NotFound />;
  if (!extract) return <div className="min-h-screen flex items-center justify-center font-body text-[#757575]" data-testid="extract-loading">Chargement…</div>;

  const sponsor = tracker.placement || (extract.partner_name ? { name: extract.partner_name, logo_url: extract.partner_logo_url } : null);
  const speechText = `${extract.title}. Auteur : ${extract.author}.\n${extractToPlainText(extract.content)}`;

  return (
    <div data-testid="page-extract-read" className="min-h-screen bg-[#FAF9F6]">
      <header className="sticky top-0 z-40 bg-[#FAF9F6]/90 backdrop-blur-md border-b border-[#D1CFC7]">
        <div className="max-w-2xl mx-auto px-5 py-3 flex items-center justify-between gap-3">
          <Link to="/" data-testid="extract-home-link"><img src="/cled-logo-cropped.png" alt="CLED" className="h-8 w-auto mix-blend-multiply" /></Link>
          <div className="flex items-center gap-2">
            <div className="flex border border-[#D1CFC7]">
              <button onClick={() => setSize(Math.max(0, size - 1))} data-testid="font-decrease-button" aria-label="Réduire le texte" className="p-2 text-[#4A4A4A] hover:text-[#2C402E] disabled:opacity-30" disabled={size === 0}><Minus size={12} /></button>
              <button onClick={() => setSize(Math.min(2, size + 1))} data-testid="font-increase-button" aria-label="Agrandir le texte" className="p-2 text-[#4A4A4A] hover:text-[#2C402E] border-l border-[#D1CFC7] disabled:opacity-30" disabled={size === 2}><Plus size={12} /></button>
            </div>
            <ListenControls text={speechText} />
          </div>
        </div>
        <div className="h-[2px] bg-[#9E4735] origin-left transition-transform duration-150" style={{ transform: `scaleX(${progress})` }} data-testid="reading-progress" />
      </header>

      <main className="max-w-2xl mx-auto px-6 pt-12 pb-32">
        {sponsor && <SponsorBadge sponsor={sponsor} />}
        <ExtractHero extract={extract} />
        <ExtractContent content={extract.content} fontSize={SIZES[size]} />
        <ExtractCTA ref={ctaRef} extract={extract} sponsor={sponsor} onTrack={tracker.track} lieu={tracker.placement?.code} offer={offer} />
        <div data-testid="extract-share" className="max-w-md mx-auto mt-10 flex justify-center">
          <SocialShareButtons url={extractShareUrl(slug, tracker.placement?.code)} title={extract.title} author={extract.author} testId="extract-share-buttons" text={`Lisez gratuitement un extrait de « ${extract.title} » de ${extract.author} (Challenges Littéraires Éditions)`} />
        </div>
        {tracker.sessionId && <ReadingSurvey key={tracker.sessionId} sessionId={tracker.sessionId} sponsor={sponsor} />}
        <NewsletterSignup slug={slug} lieu={tracker.placement?.code} sessionId={tracker.sessionId} />
        <footer className="mt-20 text-center font-body text-xs text-[#757575] space-y-2">
          <p>© Challenges Littéraires Éditions — Distributeur d'extraits littéraires CLED</p>
          <Link to="/livres" onClick={() => tracker.track("catalog")} data-testid="extract-catalog-link" className="inline-block mt-2 font-body font-semibold text-base text-[#2C402E] border-b-2 border-[#2C402E] pb-0.5 hover:text-[#9E4735] hover:border-[#9E4735] transition-colors">Découvrir tout notre catalogue</Link>
        </footer>
      </main>

      {extract.whatsapp_number && (
        <div data-testid="extract-sticky-bar" className={`fixed bottom-0 inset-x-0 z-40 bg-[#FAF9F6]/95 backdrop-blur-md border-t border-[#D1CFC7] px-5 py-3 transition-transform duration-300 ${progress > 0.12 && !ctaVisible && progress < 0.85 ? "translate-y-0" : "translate-y-full"}`}>
          <div className="max-w-2xl mx-auto"><button onClick={() => ctaRef.current?.scrollIntoView({ behavior: "smooth" })} data-testid="extract-sticky-order" className="w-full bg-[#2C402E] hover:bg-[#1F2D21] text-[#FAF9F6] py-3 font-body text-sm tracking-wide transition-colors">Commander ce livre</button></div>
        </div>
      )}
    </div>
  );
}

const SponsorBadge = ({ sponsor }) => (
  <div data-testid="extract-sponsor" className="flex items-center gap-3 mb-8 text-xs font-body text-[#4A4A4A]">
    {sponsor.logo_url && <img src={sponsor.logo_url} alt={sponsor.name} onError={(e) => { e.currentTarget.style.display = "none"; }} className="h-8 w-auto max-w-[120px] object-contain" />}
    <span>Lecture offerte par <strong className="font-medium text-[#1A1A1A]">{sponsor.name}</strong></span>
  </div>
);

const ExtractHero = ({ extract }) => (
  <section className="flex gap-6 items-end mb-14 pb-10 border-b border-[#D1CFC7] fade-in-up">
    {extract.cover_url && <img src={extract.cover_url} alt={extract.title} data-testid="extract-cover" className="w-24 sm:w-32 aspect-[3/4] object-cover shadow-[8px_8px_0_#E8E4D9] flex-shrink-0" />}
    <div className="min-w-0">
      <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E4735] font-body mb-3">Extrait offert</p>
      <h1 data-testid="extract-title" className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] font-light leading-[1.05] mb-3">
        {extract.title}
        {extract.genre && extract.genre !== "non-renseigne" && <span data-testid="extract-genre-badge" className="inline-block align-middle ml-3 -mt-1 px-2.5 py-1 border border-[#9E4735] text-[#9E4735] font-body text-[10px] sm:text-xs tracking-[0.2em] uppercase leading-none">{extractGenreLabel(extract.genre)}</span>}
      </h1>
      <p data-testid="extract-author" className="font-serif italic text-lg text-[#2C402E] mb-1"><span className="not-italic font-body text-xs uppercase tracking-[0.2em] text-[#757575] mr-2">Auteur :</span>{extract.author}</p>
      {extract.publisher && <p data-testid="extract-publisher" className="text-xs font-body text-[#4A4A4A]">{extract.publisher}</p>}
      <p className="flex items-center gap-2 text-xs font-body text-[#757575] mt-2"><Clock size={12} /> Temps de lecture : {extract.reading_time}</p>
    </div>
  </section>
);

const NotFound = () => (
  <div data-testid="extract-not-found" className="min-h-screen flex flex-col items-center justify-center px-6 text-center">
    <p className="text-[10px] tracking-[0.3em] uppercase text-[#9E4735] font-body mb-4">Extrait introuvable</p>
    <h1 className="font-serif text-4xl text-[#1A1A1A] font-light mb-6">Cet extrait n'est plus disponible.</h1>
    <Link to="/livres" className="bg-[#2C402E] text-[#FAF9F6] px-8 py-3 font-body text-sm">Voir le catalogue</Link>
  </div>
);
